import React, { useEffect } from 'react';

import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { Card, Form, DatePicker, Select, Button, Space, Checkbox } from 'antd';
import CurrencySelect from '@components/shared/selects/CurrencySelect';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * FilterBar - 銷量分析篩選條件組件
 *
 * @param {Object} props
 * @param {Array} props.channels - 可選通路列表 [{ id, name, code }]
 * @param {Array} props.currencies - 可選貨幣列表 [{ code, symbol, name, decimal_places }]
 * @param {Function} props.onQuery - 查詢回調函數
 * @param {boolean} props.loading - 載入狀態
 */
const FilterBar = ({ channels = [], currencies = [], defaultCurrency = 'TWD', onQuery, loading = false }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // 預設日期範圍：最近 30 天
  const defaultDateRange = [dayjs().subtract(30, 'days'), dayjs()];

  // 當 channels 變化時，更新預設選擇
  useEffect(() => {
    if (channels.length > 0) {
      const currentChannelIds = form.getFieldValue('channel_ids');
      if (!currentChannelIds || currentChannelIds.length === 0) {
        form.setFieldsValue({ channel_ids: channels.map((c) => c.id) });
      }
    }
  }, [channels, form]);

  const handleSubmit = (values) => {
    const [startDate, endDate] = values.dateRange || [];

    const params = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      channel_ids: values.channel_ids || [],
      currency: values.currency,
      granularity: values.granularity || 'daily',
      top_limit: 20,
    };

    onQuery(params);
  };

  const handleReset = () => {
    form.resetFields();
  };

  // 全選/取消全選通路
  const handleSelectAllChannels = (checked) => {
    if (checked) {
      form.setFieldsValue({ channel_ids: channels.map((c) => c.id) });
    } else {
      form.setFieldsValue({ channel_ids: [] });
    }
  };

  return (
    <Card size="small">
      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        initialValues={{
          dateRange: defaultDateRange,
          channel_ids: [],
          currency: defaultCurrency,
          granularity: 'daily',
        }}
        style={{ rowGap: 8, flexWrap: 'wrap' }}
      >
        <Form.Item
          name="dateRange"
          label={t('shared.analytics.filter.date_range')}
          rules={[
            { required: true, message: t('shared.analytics.filter.date_range_required') },
            {
              validator: (_, value) => {
                if (!value || value.length !== 2) {
                  return Promise.reject(t('shared.analytics.filter.date_range_required'));
                }
                const [start, end] = value;
                const days = end.diff(start, 'days');
                if (days > 180) {
                  return Promise.reject(t('shared.analytics.filter.date_range_max_180_days'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <RangePicker
            format="YYYY-MM-DD"
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            style={{ width: 260 }}
          />
        </Form.Item>

        <Form.Item
          name="channel_ids"
          label={t('shared.analytics.filter.channels')}
          rules={[{ required: true, message: t('shared.analytics.filter.channels_required') }]}
        >
          <Select
            mode="multiple"
            placeholder={t('shared.analytics.filter.channels')}
            style={{ minWidth: 200 }}
            dropdownRender={(menu) => (
              <>
                <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                  <Checkbox
                    checked={
                      channels.length > 0 &&
                      form.getFieldValue('channel_ids')?.length === channels.length
                    }
                    indeterminate={
                      form.getFieldValue('channel_ids')?.length > 0 &&
                      form.getFieldValue('channel_ids')?.length < channels.length
                    }
                    onChange={(e) => handleSelectAllChannels(e.target.checked)}
                  >
                    {t('shared.analytics.filter.select_all')}
                  </Checkbox>
                </div>
                {menu}
              </>
            )}
          >
            {channels.map((channel) => (
              <Option key={channel.id} value={channel.id}>
                {channel.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="currency" label={t('shared.analytics.filter.currency')}>
          <CurrencySelect currencies={currencies} style={{ width: 220 }} />
        </Form.Item>

        <Form.Item name="granularity" label={t('shared.analytics.filter.granularity')}>
          <Select style={{ width: 120 }}>
            <Option value="daily">{t('shared.analytics.filter.granularity_daily')}</Option>
            <Option value="weekly">{t('shared.analytics.filter.granularity_weekly')}</Option>
            <Option value="monthly">{t('shared.analytics.filter.granularity_monthly')}</Option>
          </Select>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
              {t('common.button.search')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              {t('common.button.reset')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default FilterBar;
